import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { TableFilterService } from 'app/component-library/table/filtering/table-filter.service';
import { TableColumn } from 'app/component-library/table/table-column';
import { TableConfig } from 'app/component-library/table/table-config';
import { TableParameters } from 'app/component-library/table/table-parameters';
import { ConfirmationDialogComponent } from 'app/shared/confirmation-dialog/confirmation-dialog.component';
import { MaintenanceStrategyPlannedMaintenance } from 'app/viewmodels/maintenance-strategies/maintenance-strategy-planned-maintenance';
import { ConditionForWorkPipe } from 'app/viewmodels/planned-maintenance/condition-for-work-pipe';
import { MaintenanceTypePipe } from 'app/viewmodels/planned-maintenance/maintenance-type-pipe';
import { LazyLoadEvent } from 'primeng/api';
import { TableConfigHelperService } from '../../../../shared/table-helpers/table-config-helper-service';
import { RowsPerPage, defaultIndexColumnWidth, defaultRowsPerPageOptions } from '../../../../shared/table-helpers/table-helper';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceStrategyPlannedMaintenanceTableConfigService {

  public availableItemsConfig: TableConfig;
  public loadedItemsConfig: TableConfig;

  private readonly rowNumber: string = 'rowNumber';
  private readonly reference: string = 'reference';

  private maintenanceTypePipe = new MaintenanceTypePipe(this.i18NextPipe);
  private conditionForWorkPipe = new ConditionForWorkPipe(this.i18NextPipe);

  constructor(
    private i18NextPipe: I18NextPipe,
    private tableFilterService: TableFilterService,
    private tableConfigHelperService: TableConfigHelperService,
  ) { }

  public initAvailableItemsTableConfig(): void {
    this.availableItemsConfig = this.createPaginationCollectionConfig(false, true);
  }

  public initLoadedItemsTableConfig(canModify: boolean, canSelect: boolean = false): void {
    this.loadedItemsConfig = this.createLoadedCollectionConfig(canModify, canSelect);
  }

  public createPaginationCollectionConfig(canModify: boolean, canSelect: boolean = false): TableConfig {
    const config = this.createTableConfig(canModify, canSelect);
    config.lazyLoadOnInit = true;
    config.lazyLoading = true;
    config.id = 'maintenance-strategy-available-planned-maintenance-table';
    return config;
  }

  public createLoadedCollectionConfig(canModify: boolean, canSelect: boolean = false): TableConfig {
    const config = this.createTableConfig(canModify, canSelect, true, true);
    config.lazyLoadOnInit = false;
    config.lazyLoading = false;
    config.id = 'maintenance-strategy-planned-maintenance-table';
    config.rowHighlightFunction = rowData => this.tableConfigHelperService.getRowStyleClass(rowData);
    return config;
  }

  public createTableConfig(
    canModify: boolean,
    canSelect: boolean = false,
    hasRowNumber: boolean = false,
    hideEnumColumnsFilter = false,
  ): TableConfig {
    const config = this.createBaseTableConfig();

    config.itemTypeName = this.i18NextPipe.transform('ModelPlannedMaintenanceDetails.PlannedMaintenance');
    config.startingSortField = hasRowNumber ? this.rowNumber : this.reference;
    config.columns = this.createMaintenanceTableColumns(hasRowNumber, hideEnumColumnsFilter);
    config.emptyMessage = this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.NoPlannedMaintenanceAvailable');

    this.initConfigOperationState(config, canModify, canSelect);

    return config;
  }

  private createBaseTableConfig(): TableConfig {
    const config = new TableConfig();
    config.enableEdit = false;
    config.enableDelete = false;
    config.enableFiltering = true;
    config.enablePagination = true;
    config.enableSorting = true;
    config.showRowHighlightInfoColumn = false;
    config.rowsPerPage = RowsPerPage.Medium;
    config.rowsPerPageOptions = defaultRowsPerPageOptions;
    config.useSortTransformation = true;
    return config;
  }

  private initConfigOperationState(config: TableConfig, canModify: boolean, canSelect: boolean): void {
    config.enableAdd = canModify;
    config.enableSelectionColumn = canModify || canSelect;
    this.setDeleteTableActions(config, canModify);
  }

  public updateOperationState(config: TableConfig, canModify: boolean, canSelect: boolean): void {
    if (!config) return;
    config.enableAdd = canModify;
    config.enableSelectionColumn = canModify || canSelect;
    this.updateDeleteAction(config, canModify);
  }

  public updateLoadedCollectionOperationState = (canModify: boolean) => this.updateOperationState(this.loadedItemsConfig, canModify, true);

  private createMaintenanceTableColumns(hasRowNumber: boolean = false, hideEnumColumnsFilter: boolean = false): TableColumn[] {

    const refCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Reference'), this.reference);
    const titleCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Title'), 'title');

    const typeCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Type'), 'type');
    typeCol.formatterPipe = MaintenanceTypePipe;
    typeCol.formatterPipeArgs = [this.i18NextPipe];
    typeCol.sortTransformation = value => this.maintenanceTypePipe.transform(value);
    typeCol.hideFilter = hideEnumColumnsFilter;

    const cfwCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Cfw'), 'conditionForWork');
    cfwCol.formatterPipe = ConditionForWorkPipe;
    cfwCol.formatterPipeArgs = [this.i18NextPipe];
    cfwCol.sortTransformation = value => this.conditionForWorkPipe.transform(value);
    cfwCol.hideFilter = hideEnumColumnsFilter;

    const frequencyCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Frequency'), 'frequencyValue');
    const intervalCol = new TableColumn(this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.Interval'), 'interval');

    const columns = [refCol, titleCol, typeCol, cfwCol, frequencyCol, intervalCol];

    if (hasRowNumber) {
      const rowNumberColumn = new TableColumn('#', this.rowNumber);
      rowNumberColumn.widthPx = defaultIndexColumnWidth;
      columns.unshift(rowNumberColumn);
    }

    return columns;
  }

  private setDeleteTableActions(config: TableConfig, canModify: boolean): void {
    config.actions = [];
    const deleteAction = this.tableConfigHelperService.createDeleteTableAction(config.deleteActionTitle, canModify);
    config.actions.push(deleteAction);
  }

  public createTableParameters(tableState: LazyLoadEvent, selectedItemId: string, columns: TableColumn[]): TableParameters {
    const filters = this.tableFilterService.createFilters(tableState.filters, columns) || [];
    return this.tableConfigHelperService.createTableParameters(tableState, selectedItemId, filters);
  }

  public createAvailableItemsTableParameters(tableState: LazyLoadEvent, selectedItemId: string, plannedMaintenance: MaintenanceStrategyPlannedMaintenance[]): TableParameters {
    const params = this.createTableParameters(tableState, selectedItemId, this.availableItemsConfig.columns);
    this.assignIdsToExclude(params, plannedMaintenance);
    return params;
  }

  public assignIdsToExclude(params: TableParameters, plannedMaintenance: MaintenanceStrategyPlannedMaintenance[]): void {
    if (plannedMaintenance && plannedMaintenance.length > 0) {
      params.idsToExclude = plannedMaintenance.map(pm => pm.plannedMaintenanceLibraryItemId);
    }
  }

  public setRecordCountConfigParameters(config: TableConfig, tableParams: TableParameters, totalRecords: number): void {
    const hasFilters = tableParams.filters && tableParams.filters.length > 0;
    const params = this.tableConfigHelperService.createRecordCountConfigParams(totalRecords, hasFilters);
    this.tableConfigHelperService.modifyConfig(config, params);
  }

  public setAvailableItemsRecordCountConfigParameters(tableParams: TableParameters, totalRecords: number): void {
    return this.setRecordCountConfigParameters(this.availableItemsConfig, tableParams, totalRecords);
  }

  public updateDeleteAction = (config: TableConfig, visible: boolean, enable: boolean = undefined): void =>
    this.tableConfigHelperService.updateDeleteAction(config, visible, enable);

  public startLoading = (): void => this.tableConfigHelperService.modifyConfig(this.availableItemsConfig, { loading: true });
  public finishLoading = (): void => this.tableConfigHelperService.modifyConfig(this.availableItemsConfig, { loading: false });

  public createDeleteConfirmationDialog(content: ConfirmationDialogComponent): ConfirmationDialogComponent {
    content.showCancel = true;
    content.title = this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.DeletePlannedMaintenance');
    content.messageTop = this.i18NextPipe.transform('MaintenanceStrategyPlannedMaintenanceTable.WillBeDeleted');
    return content;
  }
}
